import { isSodimacStandalone, isTottusStandalone } from '@digital-retail/store-manager';
import getConfig from 'next/config';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getTenantValue } from '../../../../containers/ProdctSpecifications/BuyTogether/constants';
import { isWindow } from '../../../../utils/deviceChecker';
import { withLabels } from '../../../../utils/LabelsContext';
import _ from '../../../../utils/LodashImports';
import { noop } from '../../../../utils/noop';
import { recoAnalytics } from '../../../../utils/recoAnalytics';
import useIntersection from '../../../../utils/useIntersection';
import useLazyLoadImage from '../../../../utils/useLazyLoadImage';
import { getMeasurements } from '../../../../utils/variant';
import { Badges } from '../../../Badges/Badges';
import CartItemControls from '../../../CartItemControls/CartItemControls';
import InstallmentsWithoutInterest from '../../../InstallmentsWithoutInterest/InstallmentsWithoutInterest';
import Prices from '../../../Prices/Prices';
import Image from '../../Image/Image';
import RatingsVisual from '../../RatingsVisual/RatingsVisual';
import Reviews from '../../Reviews/Reviews';
import { Actions, MobileCarouselPodStyles } from './MobileCarouselPod.style';
import {
  ANALYTICS_CONSTANTS,
  createAnalyticsEventData,
  createSodimacAnalyticsData,
  getSponsoredText,
  hasMinimumReviews,
  INTERSECTION_CONFIG,
  isProductSponsored,
  isRecommendedSponsored,
  shouldSendImpression,
} from './MobileCarouselPod.utils';
import { getHref } from './utils';

const { publicRuntimeConfig } = getConfig();

const MobileCarouselPod = ({
  product,
  appCtx,
  labels,
  details,
  aggregateViewedProducts,
  isSponsored,
  sponsoredClick,
  currentUserSegments,
  isUserLoggedIn,
}) => {
  const { deviceType, regionCode, store } = appCtx;
  const [loadImage, setLoadImage] = useState(false);
  const isTottusSA = isTottusStandalone(store);
  const productBrand = _.get(product, 'brand', '');
  const meatStickers = _.get(product, 'meatStickers', []);
  const hasRatingData = product.rating && Number(product.rating) > 0;
  const isMoreThanOneRating = hasRatingData || hasMinimumReviews(product.totalReviews);
  const enableSponsoredInRecommendation = _.get(appCtx, 'siteConfig.toggles.enableSponsoredInRecommendation', false);
  const isEnabledAddToCartInReco = _.get(appCtx, 'siteConfig.toggles.isEnabledAddToCartInReco', false) && isTottusSA;
  const sponsoredText = getSponsoredText(labels);
  const productSponsored = isProductSponsored(isSponsored, product);
  const adType = details?.adType || ANALYTICS_CONSTANTS.AD_TYPE;
  const recommendedSponsored = isRecommendedSponsored(enableSponsoredInRecommendation, product);
  const href = getHref(product, details, productSponsored, publicRuntimeConfig, appCtx);
  const podRef = useRef();
  const productRef = useRef(null);
  const hasSentImpression = useRef(false);
  const shouldShowPatrocinadoPod = recommendedSponsored;

  const inViewport = useIntersection(
    podRef,
    INTERSECTION_CONFIG.ROOT_MARGIN,
    INTERSECTION_CONFIG.IMPRESSION_THRESHOLD,
    INTERSECTION_CONFIG.MULTIPLE_OBSERVE
  );
  const isCarouselInViewport = useIntersection(
    productRef,
    INTERSECTION_CONFIG.ROOT_MARGIN,
    INTERSECTION_CONFIG.VIEWPORT_THRESHOLD,
    INTERSECTION_CONFIG.MULTIPLE_OBSERVE
  );
  const sendImpression = shouldSendImpression(recommendedSponsored, isSponsored, inViewport, product?.adInfo);

  // Memoize expensive calculations
  const measurement = useMemo(
    () =>
      getMeasurements({
        product,
        productMeasurement: product.measurement,
      }),
    [product]
  );

  // Memoize stable values to prevent unnecessary re-renders
  const stableProductData = useMemo(
    () => ({
      productId: product.productId,
      adInfo: product?.adInfo,
      providerName: product?.providerName,
    }),
    [product.productId, product?.adInfo, product?.providerName]
  );

  const stableDetailsData = useMemo(
    () => ({
      productPosition: details?.productPosition,
    }),
    [details?.productPosition]
  );

  // Memoize sponsored click handler to prevent recreations
  const handleSponsoredImpression = useCallback(() => {
    if (sendImpression && !hasSentImpression.current) {
      sponsoredClick(
        ANALYTICS_CONSTANTS.IMPRESSION_EVENT,
        stableProductData.productId,
        stableProductData.adInfo,
        stableDetailsData.productPosition,
        stableProductData.providerName,
        adType,
        stableProductData.productId
      );
      hasSentImpression.current = true;
    }
  }, [sendImpression, sponsoredClick, stableProductData, stableDetailsData, adType]);

  useEffect(() => {
    handleSponsoredImpression();
  }, [handleSponsoredImpression]);

  // Memoize viewport tracking handler
  const handleViewportTracking = useCallback(() => {
    if (isWindow() && aggregateViewedProducts && isCarouselInViewport) {
      aggregateViewedProducts(product.id);
    }
  }, [aggregateViewedProducts, isCarouselInViewport, product.id]);

  useEffect(() => {
    handleViewportTracking();
  }, [handleViewportTracking]);

  useLazyLoadImage({ productRef, setLoadImage });

  const areSponsoredClickEventsEnabledWithQueryParams = _.get(
    appCtx,
    'siteConfig.toggles.areSponsoredClickEventsEnabledWithQueryParams',
    false
  );

  const redirectToProduct = useCallback(() => {
    const eventData = createAnalyticsEventData(details, product, appCtx, getTenantValue);

    if (isSponsored || recommendedSponsored) {
      if (!areSponsoredClickEventsEnabledWithQueryParams) {
        sponsoredClick(
          ANALYTICS_CONSTANTS.CLICK_EVENT,
          product.productId,
          product.adInfo,
          details.productPosition,
          product?.providerName,
          adType,
          product.productId
        );
      }
      sponsoredClick(
        ANALYTICS_CONSTANTS.VISIT_EVENT,
        product.productId,
        product.adInfo,
        details.productPosition,
        product?.providerName,
        adType
      );
    } else if (isSodimacStandalone(appCtx.store)) {
      const sodimacAnalyticsData = createSodimacAnalyticsData(details, product, appCtx, getTenantValue);
      recoAnalytics.click(sodimacAnalyticsData);
      return;
    }

    const eventToDigitalData = new CustomEvent('DDPDPCarouselProductClick', {
      bubbles: true,
      detail: eventData,
    });
    window.dispatchEvent(eventToDigitalData);
  }, [
    details,
    product,
    appCtx,
    isSponsored,
    recommendedSponsored,
    areSponsoredClickEventsEnabledWithQueryParams,
    sponsoredClick,
    adType,
  ]);

  const imageSrc = product.mediaUrls && product.mediaUrls[0];

  const actions = isEnabledAddToCartInReco && (
    <div className="pod-actions">
      <CartItemControls product={product} />
      <style jsx>{Actions}</style>
    </div>
  );

  const imagePod = (image) =>
    isEnabledAddToCartInReco ? (
      <a onClick={redirectToProduct} href={href}>
        {image}
      </a>
    ) : (
      image
    );

  const carousel = (
    <div className="carousel__pod-content">
      <div className="carousel__pod-image">
        {meatStickers && (
          <div className="badges-container">
            <Badges badgeItems={meatStickers.slice(0, 2)} deviceType={deviceType} layout="POD" isSticker />
          </div>
        )}
        {loadImage &&
          imagePod(
            <Image
              id={product.id}
              width={240}
              height={240}
              alt={product.displayName}
              useSrcSet={false}
              inlineDimensions={false}
              imageUrl={imageSrc}
              lazyLoad
            />
          )}
      </div>

      <div className="carousel__pod-layout">
        <div className="carousel__pod-details">
          <div className="carousel__pod-details--top">
            <div className="carousel__pod-brand">{productBrand}</div>
            <div className="carousel__pod-description">{product.displayName}</div>
            {isMoreThanOneRating && (
              <div className="carousel__pod-rating">
                <RatingsVisual value={Number(product.rating)} productId={product.productId} size="normal" />
                <Reviews value={Number(product.totalReviews)} />
              </div>
            )}
            <div className="carousel__pod-price">
              <Prices
                priceList={product.prices}
                productId={product.productId}
                page="POD"
                layout="4_GRID"
                variant="DESKTOP_4_GRID"
                discountBadge={product.discountBadge}
                isSponsoredProduct={isSponsored}
                measurement={measurement}
                regionCode={regionCode}
              />
            </div>
            <InstallmentsWithoutInterest
              appCtx={appCtx}
              product={product}
              userSegments={currentUserSegments}
              isUserLoggedIn={isUserLoggedIn}
            />
          </div>
          <div className="carousel__pod-details--bottom">
            {shouldShowPatrocinadoPod && <div className="carousel__pod-sponsored-label">{sponsoredText}</div>}
          </div>
        </div>
      </div>

      {actions}

      <style jsx>{MobileCarouselPodStyles}</style>
    </div>
  );

  const linkToProduct = (children) => (
    <div className="carousel__pod" ref={productRef}>
      <a onClick={redirectToProduct} className="carousel__pod-anchor" href={href} aria-hidden="true" ref={podRef}>
        {children}
      </a>
      <style jsx>{MobileCarouselPodStyles}</style>
    </div>
  );

  const addToCart = (children) => (
    <div className="carousel-pod-wrapper" ref={productRef}>
      {children}
      <style jsx>{MobileCarouselPodStyles}</style>
    </div>
  );

  return (
    <>
      {isEnabledAddToCartInReco ? addToCart(carousel) : linkToProduct(carousel)}
      <style jsx>{MobileCarouselPodStyles}</style>
    </>
  );
};

MobileCarouselPod.propTypes = {
  product: PropTypes.object.isRequired,
  appCtx: PropTypes.object.isRequired,
  labels: PropTypes.object,
  details: PropTypes.object.isRequired,
  aggregateViewedProducts: PropTypes.func.isRequired,
  isSponsored: PropTypes.bool,
  sponsoredClick: PropTypes.func,
  currentUserSegments: PropTypes.array,
  isUserLoggedIn: PropTypes.bool,
};

MobileCarouselPod.defaultProps = {
  labels: { ADD_TO_CART: 'Ver producto' },
  isSponsored: false,
  sponsoredClick: noop,
  currentUserSegments: [],
  isUserLoggedIn: false,
};

export { MobileCarouselPod };
export default withLabels(MobileCarouselPod);
